import { Rocket, LayoutGrid, Sparkles, Upload, FileText, Presentation } from "lucide-react";

export const HELP_CATEGORIES = [
  {
    key: "getting-started",
    title: "Getting Started",
    icon: Rocket,
    description: "Sign up, verify your email, and create your first project.",
    articles: [
      {
        key: "signup-and-verify",
        title: "Signing Up & Verifying Your Email",
        preview: "signup",
        steps: [
          "Go to the Sign Up page and enter your name, email, and a password.",
          "Click \u201cCreate Account.\u201d Your account is created but inactive until you verify it.",
          "Check your inbox for an email from ModernTect and click the verification link inside it.",
          "Once verified, go back to the Log In page and sign in with your email and password.",
          "If you don't see the email, use \u201cResend verification email\u201d on the login screen after a failed login attempt.",
        ],
      },
      {
        key: "first-project",
        title: "Creating Your First Project",
        preview: "dashboard",
        steps: [
          "From the Dashboard, click \u201cNew Project.\u201d",
          "Give it a name and confirm \u2014 you'll land straight in the Workbench with an empty canvas.",
          "On the Free plan you can have 1 active project. Delete a project or request an upgrade to create more.",
          "Use the sidebar tree on the left to add your first elements, or import an existing model (see \u201cImporting Your Model\u201d).",
        ],
      },
    ],
  },
  {
    key: "modeling",
    title: "Modeling on the Canvas",
    icon: LayoutGrid,
    description: "Add elements, connect them, group them, and keep the layout tidy.",
    articles: [
      {
        key: "add-connect-elements",
        title: "Adding & Connecting Elements",
        preview: "canvas",
        steps: [
          "Click \u201c+ Element\u201d in the toolbar, choose a layer (Business, Application, Technology...) and a type, then name it.",
          "The new element appears on the current view \u2014 drag it anywhere on the canvas.",
          "To connect two elements, drag from the edge of one node to another; pick the relationship type when prompted.",
          "Double-click any element to open its details panel and edit its name, status, owner, or other metadata.",
          "Use the sidebar tree to browse every element in the project, even ones not on the current view \u2014 double-click one there to drop it onto the active view.",
        ],
      },
      {
        key: "grouping-tidy-up",
        title: "Grouping & Tidy Up",
        preview: "grouping",
        steps: [
          "Shift+click multiple elements on the canvas to multi-select them.",
          "Click \u201cGroup Selected\u201d to wrap them in a resizable, labeled group box.",
          "Click \u201cSuggest Groupings\u201d to let the app propose logical groups once you have several ungrouped elements \u2014 accept or reject each suggestion.",
          "Click \u201cTidy Up\u201d at any time to auto-arrange the whole view into a clean, non-overlapping layout.",
          "Click a group's header to collapse or expand it, or use the ungroup icon to dissolve a group without deleting its contents.",
        ],
      },
    ],
  },
  {
    key: "ai-assistant",
    title: "The AI Assistant",
    icon: Sparkles,
    description: "Chat with the assistant to build or change your model in plain English.",
    articles: [
      {
        key: "chat-with-assistant",
        title: "Chatting With the Assistant to Build or Change Your Model",
        preview: "chat",
        steps: [
          "Open the chat panel on the right of the Workbench (or tap the chat icon on mobile).",
          "Type a request in plain English, e.g. \u201cAdd a payment gateway application component\u201d or \u201cConnect the CRM to the billing system.\u201d",
          "The assistant proposes the change \u2014 review what it plans to add, rename, or connect.",
          "Click Approve to apply it to your model, or Reject to discard the suggestion and try a different prompt.",
          "Use the starter prompt chips for common requests if you're not sure what to ask.",
        ],
      },
    ],
  },
  {
    key: "importing",
    title: "Importing Your Model",
    icon: Upload,
    description: "Bring in an existing .archimate file, or draft a model from plain text.",
    articles: [
      {
        key: "import-file-or-text",
        title: "Importing a .archimate File or Generating One From Text",
        preview: "import",
        steps: [
          "Click \u201cImport\u201d in the Workbench toolbar.",
          "To import an existing model, choose \u201cArchiMate File\u201d and upload your .archimate export.",
          "To draft a new model instead, choose \u201cFrom Text,\u201d paste a description of your architecture, and let the assistant propose elements and relationships.",
          "Review the proposed elements, then confirm to add them to your project.",
          "Imported elements land in the sidebar tree \u2014 add them to a view the same way as any other element.",
        ],
      },
    ],
  },
  {
    key: "reports",
    title: "Reports & Gap Analysis",
    icon: FileText,
    description: "Generate documentation and gap analysis reports, and know how to read them.",
    articles: [
      {
        key: "generate-read-reports",
        title: "Generating and Reading Reports",
        preview: "report",
        steps: [
          "Click \u201cGenerate Report\u201d in the Workbench toolbar for a full documentation report of every view and element in the project (PDF or Word).",
          "Click \u201cGap Analysis\u201d for a report that highlights isolated elements and thinly-populated layers in your model.",
          "Reports include each element's status, owner, and release candidate metadata, plus a colored legend matching the canvas status dots.",
          "Downloads start automatically once generation finishes \u2014 larger models with many views take a little longer.",
        ],
      },
    ],
  },
  {
    key: "simple-view",
    title: "Simple View",
    icon: Presentation,
    description: "A plain-language, read-only diagram for stakeholders who don't know ArchiMate.",
    articles: [
      {
        key: "using-simple-view",
        title: "Using the Stakeholder-Friendly Diagram",
        preview: "simpleview",
        steps: [
          "Click \u201cSimple View\u201d in the Workbench toolbar.",
          "Every element in the project is automatically grouped into one of six plain-language categories: User, Process, System, Data, Infrastructure, or Other.",
          "This view is read-only \u2014 you can pan and zoom, but not drag, resize, or edit anything, so it's safe to share on a screen share.",
          "Click \u201cDownload PNG\u201d to export the diagram as an image for a slide deck or document.",
          "Click \u201cExit Simple View\u201d to return to normal ArchiMate editing.",
        ],
      },
    ],
  },
];

export function findArticle(categoryKey, articleKey) {
  const category = HELP_CATEGORIES.find((c) => c.key === categoryKey);
  const article = category?.articles.find((a) => a.key === articleKey);
  return { category, article };
}
